/**
 * Mystery Gift — auto add/remove + quantity lock
 * Adds the Mystery NBA Basketball Card (free gift) to the cart once the
 * cart subtotal (excluding the gift itself) reaches the threshold below,
 * and removes it automatically if the subtotal drops back under it.
 *
 * Quantity lock (Oct 2026): the gift is always exactly 1. If the cart ever
 * holds more than 1 (or more than one gift line), it is corrected back to a
 * single unit. Customers cannot change its quantity or remove it from the
 * cart UI, and cannot add it themselves from a product page / quick view.
 * Visual lock lives in assets/mystery-gift.css.
 *
 * Theme-agnostic: talks directly to Shopify's Cart AJAX API, so it works
 * regardless of which cart_type (drawer / notification / page) is active.
 */
(function () {
  var GIFT_VARIANT_ID = 48393186803902; // Mystery NBA Basketball Card
  var GIFT_PRODUCT_ID = 8902476529854;
  var GIFT_QTY = 1;
  var THRESHOLD_MAJOR_UNITS = 50; // AED
  var GIFT_PROPERTY_KEY = '_mystery_gift';
  var CART_UPDATE_EVENT = 'cart-update'; // matches PUB_SUB_EVENTS.cartUpdate in this theme's constants.js

  // Our own cart calls use the native fetch so they don't re-trigger the
  // cart-mutation watcher below.
  var nativeFetch = window.fetch.bind(window);

  var syncing = false;
  var pendingResync = false;

  function jsonPost(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  function fetchCart() {
    return nativeFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  }

  function findGiftLines(cart) {
    var lines = [];
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === GIFT_VARIANT_ID) lines.push(cart.items[i]);
    }
    return lines;
  }

  function nonGiftSubtotal(cart) {
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.variant_id !== GIFT_VARIANT_ID) {
        total += item.line_price;
      }
    }
    return total / 100;
  }

  function addGift() {
    var props = {};
    props[GIFT_PROPERTY_KEY] = 'true';
    return jsonPost('/cart/add.js', {
      items: [{ id: GIFT_VARIANT_ID, quantity: GIFT_QTY, properties: props }],
    }).catch(function (err) {
      console.error('[Mystery Gift] failed to add gift', err);
    });
  }

  // Set gift lines to the given quantities in one call: { lineKey: qty, ... }
  function updateGiftLines(updates) {
    return jsonPost('/cart/update.js', { updates: updates }).catch(function (err) {
      console.error('[Mystery Gift] failed to update gift', err);
    });
  }

  function refreshCartUI() {
    // The theme's cart-drawer / cart-items components re-render on the global
    // pub/sub "cart-update" event. PUB_SUB_EVENTS is a `const` (not on window),
    // so the event string is used directly.
    try {
      if (typeof window.publish === 'function') {
        window.publish(CART_UPDATE_EVENT, { source: 'mystery-gift' });
      }
    } catch (e) {
      /* no-op: theme may not expose this */
    }
    // Cart page (no drawer re-render): reload so the corrected quantity shows.
    if (window.location.pathname.replace(/\/$/, '').split('/').pop() === 'cart' && !document.querySelector('cart-drawer')) {
      window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent('mystery-gift:cart-changed'));
  }

  function syncGift() {
    if (syncing) {
      pendingResync = true;
      return Promise.resolve();
    }
    syncing = true;

    return fetchCart()
      .then(function (cart) {
        var subtotal = nonGiftSubtotal(cart);
        var giftLines = findGiftLines(cart);
        var updates = {};
        var needsUpdate = false;

        if (subtotal >= THRESHOLD_MAJOR_UNITS) {
          if (giftLines.length === 0) {
            return addGift().then(refreshCartUI);
          }
          // Keep exactly one line at quantity 1; drop any extra gift lines.
          for (var i = 0; i < giftLines.length; i++) {
            var target = i === 0 ? GIFT_QTY : 0;
            if (giftLines[i].quantity !== target) {
              updates[giftLines[i].key] = target;
              needsUpdate = true;
            }
          }
        } else {
          for (var j = 0; j < giftLines.length; j++) {
            updates[giftLines[j].key] = 0;
            needsUpdate = true;
          }
        }

        if (needsUpdate) {
          return updateGiftLines(updates).then(refreshCartUI);
        }
      })
      .catch(function (err) {
        console.error('[Mystery Gift] sync failed', err);
      })
      .finally(function () {
        syncing = false;
        if (pendingResync) {
          pendingResync = false;
          setTimeout(syncGift, 250);
        }
      });
  }

  /* ---------- UI lock (cart drawer, cart page) ---------- */
  function lockGiftUI() {
    var inputs = document.querySelectorAll('input.quantity-input[data-quantity-variant-id="' + GIFT_VARIANT_ID + '"]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      input.readOnly = true;
      input.tabIndex = -1;
      input.setAttribute('aria-readonly', 'true');
      input.min = String(GIFT_QTY);
      input.max = String(GIFT_QTY);
      var line = input.closest('.cart-item');
      if (line) {
        line.classList.add('is-mystery-gift');
        var buttons = line.querySelectorAll('.quantity-button, cart-remove-button a, cart-remove-button button');
        for (var b = 0; b < buttons.length; b++) {
          buttons[b].setAttribute('disabled', 'disabled');
          buttons[b].setAttribute('aria-hidden', 'true');
          buttons[b].tabIndex = -1;
        }
      }
    }
  }

  // Swallow clicks / key edits on the gift's stepper or remove link even if
  // CSS hasn't applied (capture phase runs before the theme's handlers).
  function isGiftControl(el) {
    if (!el || !el.closest) return false;
    var line = el.closest('.cart-item');
    if (!line || !line.querySelector('input[data-quantity-variant-id="' + GIFT_VARIANT_ID + '"]')) return false;
    return !!el.closest('.quantity-button, cart-remove-button, input.quantity-input');
  }

  document.addEventListener(
    'click',
    function (event) {
      if (isGiftControl(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  ['change', 'input', 'keydown'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        var t = event.target;
        if (t && t.matches && t.matches('input.quantity-input[data-quantity-variant-id="' + GIFT_VARIANT_ID + '"]')) {
          if (type === 'keydown' && event.key === 'Tab') return;
          event.preventDefault();
          event.stopImmediatePropagation();
          t.value = GIFT_QTY;
        }
      },
      true
    );
  });

  /* ---------- Block adding the gift manually (product page, quick view, cards) ---------- */
  function formAddsGift(form) {
    if (!form || !form.querySelector) return false;
    var idInput = form.querySelector('input[name="id"]');
    if (idInput && String(idInput.value) === String(GIFT_VARIANT_ID)) return true;
    var pf = form.closest && form.closest('product-form');
    return !!(pf && String(pf.getAttribute('data-product-id')) === String(GIFT_PRODUCT_ID));
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (form && form.action && form.action.indexOf('/cart/add') !== -1 && formAddsGift(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (form && form.action && (form.action.indexOf('/cart/add') !== -1 || form.action.indexOf('/cart') !== -1)) {
        setTimeout(syncGift, 800);
      }
    },
    true
  );

  /* ---------- Watch every cart change made by the theme ---------- */
  window.fetch = function () {
    var args = arguments;
    var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
    var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
    var result = nativeFetch.apply(window, args);
    if (isCartMutation) {
      result
        .then(function () {
          setTimeout(syncGift, 300);
        })
        .catch(function () {});
    }
    return result;
  };

  var lockQueued = false;
  var observer = new MutationObserver(function () {
    if (lockQueued) return;
    lockQueued = true;
    requestAnimationFrame(function () {
      lockQueued = false;
      lockGiftUI();
    });
  });

  function init() {
    lockGiftUI();
    observer.observe(document.body, { childList: true, subtree: true });
    syncGift();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  setInterval(syncGift, 6000);
})();
